import type { GrantablePermission, PaidModule, PermissionArea } from '@palaccount/contracts';
import { create } from 'zustand';

import { useMe } from '@/lib/session';
import { useViewAs } from '@/lib/view-as';

/**
 * Permission-aware rendering.
 *
 * Visibility is REAL knowledge: /api/me carries the session's effective
 * permission list and the company's paid modules, so a screen the caller
 * cannot reach is never offered in the first place. The Owner flag
 * short-circuits to visible (BR-PERM-66 — the owner's list is empty on
 * purpose; the flag is the grant). The Phase-3 403-learning store stays as the
 * safety net underneath: a permission revoked MID-SESSION earns one 403, is
 * recorded here, and the control disappears without waiting for the /api/me
 * cache to refresh.
 *
 * ── NOTHING IN THIS FILE IS A SECURITY CONTROL ─────────────────────────────
 *
 * Say it plainly, because the shape of the code invites the opposite reading.
 * BR-PERM-50: "Hiding a control must never be the only thing that prevents an
 * action. Every action a screen withholds must have a real check behind it
 * that refuses the action whatever the client drew, because the code the
 * hidden button called is still reachable." BR-PERM-51 says the same about
 * routes: "Navigation filtering is a display convenience… A route must never
 * rely on its menu entry being hidden."
 *
 * The controls are `PermissionGuard` and `EntitlementsGuard` on the server,
 * they run on every request, and this wave changed neither. If every function
 * in this file returned `true` the product would be exactly as safe and merely
 * less honest — which is precisely the state it was in before, drawing
 * Manufacturing for companies that had never bought it and Payroll for people
 * who hold nothing.
 *
 * ── G-08 "VIEW AS" IS AN OVERRIDE LAYER ON TOP, AND STILL NOT A CONTROL ────
 *
 * While the Owner previews another person (`lib/view-as.ts`), `useCan` and
 * `useCapabilities` answer from THAT person's permission list with
 * `isOwner=false`, so the product redraws as they would see it. The 403 store
 * is bypassed for the preview — its entries belong to the real session, and no
 * real 403 can arise from a preview whose writes never leave the browser. The
 * server session is untouched throughout; see view-as.ts.
 */
interface PermissionState {
  denied: ReadonlySet<string>;
  deny: (permission: GrantablePermission) => void;
  reset: () => void;
}

export const usePermissionStore = create<PermissionState>((set) => ({
  denied: new Set<string>(),
  deny: (permission) =>
    set((state) =>
      state.denied.has(permission) ? state : { denied: new Set([...state.denied, permission]) },
    ),
  reset: () => set({ denied: new Set<string>() }),
}));

/**
 * Whether the current session should be OFFERED an action gated by
 * `permission`. Owner → always; a recorded 403 → never (revocation beats a
 * stale grant list); otherwise the /api/me grant list answers. While the
 * session is still loading, offer optimistically — the server enforces
 * regardless, and a flash of a forbidden button beats hiding legal work.
 */
export function useCan(permission: GrantablePermission): boolean {
  const { data: me } = useMe();
  const denied = usePermissionStore((state) => state.denied);
  const viewAs = useViewAs();
  // G-08: the previewed person's list is the whole answer.
  if (viewAs !== null) return viewAs.permissions.includes(permission);
  if (me?.user.isOwner) return true;
  if (denied.has(permission)) return false;
  if (me) return (me.permissions as readonly string[]).includes(permission);
  return true;
}

/**
 * Whether the company HAS a paid module (§1.5.3).
 *
 * Not a permission and never treated as one: "A switched-off module is off for
 * the whole company and for every user in it, whatever their role. No
 * permission brings it back." So the Owner flag does NOT short-circuit this —
 * an owner whose company never bought Manufacturing must not be shown
 * Manufacturing either, and the server would refuse them exactly as it refuses
 * anybody else.
 *
 * Optimistic while the session loads, for the same reason `useCan` is.
 */
export function useHasModule(module: PaidModule): boolean {
  const { data: me } = useMe();
  if (!me) return true;
  return (me.modules as readonly string[]).includes(module);
}

/**
 * BR-PERM-43 — "An area a role holds nothing on must have its menu group
 * hidden entirely, not greyed… For a user with no role, every menu group is
 * hidden on the same rule."
 *
 * Takes the whole session rather than calling a hook per area, so a caller
 * filtering thirteen groups makes one pass and obeys the rules of hooks.
 */
export interface SessionCapabilities {
  readonly isOwner: boolean;
  readonly permissions: ReadonlySet<string>;
  readonly modules: ReadonlySet<string>;
  /** True until /api/me resolves — callers draw optimistically, as above. */
  readonly loading: boolean;
}

export function useCapabilities(): SessionCapabilities {
  const { data: me } = useMe();
  const denied = usePermissionStore((state) => state.denied);
  const viewAs = useViewAs();
  if (!me) {
    return { isOwner: false, permissions: new Set(), modules: new Set(), loading: true };
  }
  if (viewAs !== null) {
    // G-08: the previewed person's grants, never the Owner flag. Modules are
    // the COMPANY's and stay as they are (§1.5.3 — no permission changes them).
    return {
      isOwner: false,
      permissions: new Set(viewAs.permissions),
      modules: new Set(me.modules as readonly string[]),
      loading: false,
    };
  }
  const granted = (me.permissions as readonly string[]).filter(
    (permission) => !denied.has(permission),
  );
  return {
    isOwner: me.user.isOwner,
    permissions: new Set(granted),
    modules: new Set(me.modules as readonly string[]),
    loading: false,
  };
}

/** Does the session hold ANY grant in this area? (BR-PERM-43's own wording.) */
export function holdsArea(caps: SessionCapabilities, area: PermissionArea): boolean {
  if (caps.isOwner) return true;
  const prefix = `${area}.`;
  for (const permission of caps.permissions) if (permission.startsWith(prefix)) return true;
  return false;
}

export function holds(caps: SessionCapabilities, permission: GrantablePermission): boolean {
  return caps.isOwner || caps.permissions.has(permission);
}

/** §1.5.3 — the module question. The Owner flag is not an answer to it. */
export function hasModule(caps: SessionCapabilities, module: PaidModule): boolean {
  return caps.modules.has(module);
}

/** Records a 403 against the permission the failed route is annotated with. */
export function denyPermission(permission: GrantablePermission): void {
  usePermissionStore.getState().deny(permission);
}

/** Cleared on sign-out — the next session may hold a different role. */
export function resetPermissions(): void {
  usePermissionStore.getState().reset();
}
